import type { TemplateId } from './types'

/**
 * Plain-language descriptions of the Autom8 automation templates, written for
 * business users rather than the admins who configured them. Ids (and the
 * default folder paths in automations.ts) must stay in sync with the admin
 * app's template registry and the runner's defaults.
 */

export interface TemplateInfo {
  id: TemplateId
  title: string
  /** What this automation does, in words a non-technical user understands. */
  blurb: string
  category: string
  /** Present when users can send files to this automation. */
  fileDrop?: {
    prompt: string
    /** Lowercase dot-prefixed extensions the runner can process. */
    accept: string[]
  }
  /**
   * Shown in the Results section before any output exists, so first-time
   * users know where output will appear. Present exactly when the template
   * produces downloadable results (see resultsTargetFor).
   */
  resultsHint?: string
}

export const TEMPLATE_INFO: readonly TemplateInfo[] = [
  {
    id: 'website-uptime',
    title: 'Website uptime check',
    blurb: 'Keeps an eye on your website and raises an alert if it stops responding.',
    category: 'Monitoring'
  },
  {
    id: 'gcs-backup',
    title: 'File backup',
    blurb: 'Copies your company files to a backup location on a schedule.',
    category: 'Backup'
  },
  {
    id: 'gcs-cleanup',
    title: 'Old file cleanup',
    blurb: 'Tidies up files older than your retention window to keep storage costs down.',
    category: 'Maintenance'
  },
  {
    id: 'http-task',
    title: 'Scheduled task',
    blurb: 'Runs a scheduled task against one of your online services.',
    category: 'Integration'
  },
  {
    id: 'file-process',
    title: 'Spreadsheet cleaner',
    blurb:
      'Send in CSV or Excel files and it cleans them up — filtering rows, tidying columns, removing duplicates — into ready-to-use files.',
    category: 'Spreadsheets',
    fileDrop: {
      prompt: 'Drop CSV or Excel files here',
      accept: ['.csv', '.xlsx']
    },
    resultsHint: 'Cleaned files appear here after a run.'
  },
  {
    // Deliberately says nothing about Python. The card normally shows the name
    // the administrator gave it ("Payroll reconciler"), so this title is only
    // a fallback — and naming the implementation would only prompt questions a
    // business user cannot act on.
    id: 'python-script',
    title: 'Custom spreadsheet step',
    blurb:
      'Send in CSV or Excel files and it runs your company\'s own processing steps on them — the ones set up specially for your business.',
    category: 'Spreadsheets',
    fileDrop: {
      prompt: 'Drop CSV or Excel files here',
      accept: ['.csv', '.xlsx']
    },
    resultsHint: 'Processed files appear here after a run.'
  }
] as const

export function templateInfo(id: string): TemplateInfo | undefined {
  return TEMPLATE_INFO.find((t) => t.id === id)
}
